// src/components/EditProject.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import Axios from "../../utils/Axios";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../../styles/AddProject.css";
import validateForm from "../../utils/Validation";
import AuthHandler from "../../utils/AuthHandler";
import { EDIT_PROJECT } from "../../config/API_Collection";
import SearchableDropdown from "../../components/DropdownSearch";
import {
  UPLOAD_PROJECT_FILE_REQUEST,
  CLEAR_UPLOADED_FILES,
  REMOVE_UPLOADED_FILE,
  FETCH_EMPLOYEES_REQUEST,
} from "../../store/Actions/Actions";

function EditProject({ closeModalEdit, showToast, projectData = {}, update }) {
  const navigate = useNavigate();
  const DISPATCH = useDispatch();

  // Live data
  const employees = useSelector((s) => s?.employees?.employees) || [];
  const uploadState = useSelector((_) => _?.upload);

  // Local data
  const [clients, setClients] = useState([]);
  const [toast, setToast] = useState({ message: "", type: "" });
  const [attachments, setAttachments] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [progress, setProgress] = useState({});
  const [customNames, setCustomNames] = useState([]);
  const [uploadTargetIndex, setUploadTargetIndex] = useState(0);
  const prevUploadedCountRef = useRef(0);

  // Form state
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Dates
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Controlled values (string ids for hidden inputs)
  const [clientId, setClientId] = useState("");
  const [pmId, setPmId] = useState("");
  const [pricing, setPricing] = useState("");
  const [office, setOffice] = useState("");
  const [marketSegment, setMarketSegment] = useState("");

  const clearError = (name) => {
    if (formErrors?.[name]) {
      const next = { ...formErrors };
      delete next[name];
      setFormErrors(next);
    }
  };

  // File upload handlers
  const handleFileSelect = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) handleFiles([file], uploadTargetIndex);
    e.target.value = "";
  };

  const handleFiles = (files, slotIndex = undefined) => {
    files.forEach((file) => {
      const baseName =
        typeof slotIndex === "number" && customNames[slotIndex]
          ? customNames[slotIndex].trim()
          : "";

      const file_name = baseName || file.name;

      // Initialize progress at 0 and start smooth animation
      setProgress((prev) => ({ ...prev, [file_name]: 0 }));

      // Simulate smooth progress from 0 to 90% over ~2.5 seconds
      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          const current = prev[file_name] || 0;
          if (current >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          // Increment by 5-10% each 200ms for smooth animation
          const increment = Math.random() * 5 + 5;
          return { ...prev, [file_name]: Math.min(90, current + increment) };
        });
      }, 200);

      DISPATCH({
        type: UPLOAD_PROJECT_FILE_REQUEST,
        payload: { file, file_name, section: "project", slotIndex },
      });

      if (typeof slotIndex === "number") {
        setUploadingFiles((prev) => {
          const arr = [...prev];
          while (arr.length <= slotIndex) arr.push(null);
          arr[slotIndex] = { name: file_name, originalFile: file };
          return arr;
        });
      } else {
        setUploadingFiles((prev) => [
          ...prev,
          { name: file_name, originalFile: file },
        ]);
      }
    });
  };

  const handleFileRemove = (
    fileName,
    slotIndex = undefined,
    isExisting = false
  ) => {
    if (isExisting) {
      // Remove from existing attachments
      setAttachments((prev) => prev.filter((_, i) => i !== slotIndex));
    } else {
      // Remove from uploading files
      let localIndex = -1;
      if (typeof slotIndex === "number") {
        localIndex = slotIndex;
      } else {
        localIndex = uploadingFiles.findIndex((f) => f && f.name === fileName);
      }

      setUploadingFiles((prev) => {
        const arr = [...prev];
        if (localIndex !== -1 && localIndex < arr.length) {
          arr.splice(localIndex, 1);
        } else if (fileName) {
          return arr.filter((f) => f && f.name !== fileName);
        }
        return arr.filter(Boolean);
      });

      setCustomNames((prev) => {
        const arr = [...prev];
        if (localIndex !== -1 && localIndex < arr.length) {
          arr.splice(localIndex, 1);
        }
        return arr.filter((v) => v !== undefined && v !== null);
      });

      setProgress((prev) => {
        const updated = { ...prev };
        if (fileName && updated[fileName]) delete updated[fileName];
        return updated;
      });

      let uploadedMatch = null;
      if (typeof slotIndex === "number") {
        uploadedMatch = (uploadState?.uploaded || []).find(
          (u) => typeof u?._slotIndex === "number" && u._slotIndex === slotIndex
        );
      }
      if (!uploadedMatch && fileName) {
        uploadedMatch =
          uploadState?.uploaded?.find((u) => u?.file_name === fileName) || null;
      }

      if (uploadedMatch) {
        const sendName =
          uploadedMatch.file_name ||
          uploadedMatch.fileName ||
          uploadedMatch.filename ||
          uploadedMatch.name;
        DISPATCH({
          type: REMOVE_UPLOADED_FILE,
          payload: {
            file_name: sendName,
            document_id: uploadedMatch.document_id,
            slotIndex: localIndex,
          },
        });
      }
    }
  };

  const getProgressBarColor = (value) => {
    if (value <= 0) return "progress-bar bg-light";
    if (value < 100) return "progress-bar bg-primary";
    return "progress-bar bg-primary";
  };

  const getDisplayProgress = (name) => {
    const p = progress?.[name];
    if (typeof p === "number") return p;
    const uploadedMatch = uploadState?.uploaded?.find(
      (d) => d?.file_name === name
    );
    if (uploadedMatch) return 100;
    if (uploadingFiles.find((f) => f && f.name === name)) return 0;
    return 0;
  };

  // Fetch clients once
  useEffect(() => {
    (async () => {
      try {
        const res = await Axios.get("clients/client_list");
        if (res.status === 200) setClients(res?.data?.data || []);
      } catch (error) {
        AuthHandler(error.response?.status, setToast, navigate);
      }
    })();
  }, [navigate]);

  // Fetch employees list when component mounts
  useEffect(() => {
    DISPATCH({ type: FETCH_EMPLOYEES_REQUEST });
  }, [DISPATCH]);

  // Toast feedback for upload success
  useEffect(() => {
    const current = uploadState?.uploaded?.length || 0;
    const prev = prevUploadedCountRef.current;
    if (current > prev) {
      showToast("File uploaded successfully", "success");
    }
    prevUploadedCountRef.current = current;
  }, [uploadState?.uploaded, showToast]);

  // Update progress when files are uploaded
  useEffect(() => {
    const uploaded = uploadState?.uploaded || [];
    if (!uploaded || uploaded.length === 0) return;

    setProgress((prev) => {
      const updated = { ...prev };
      uploaded.forEach((u) => {
        if (typeof u?._slotIndex === "number") {
          const idx = u._slotIndex;
          const slotEntry = uploadingFiles[idx];
          if (slotEntry && slotEntry.name) {
            updated[slotEntry.name] = 100;
            return;
          }
        }

        const slotIndex = uploadingFiles.findIndex(
          (f) =>
            f &&
            (f.name === u.file_name || f.originalFile?.name === u.file_name)
        );
        if (slotIndex !== -1) {
          const slotName = uploadingFiles[slotIndex].name;
          updated[slotName] = 100;
        } else if (u.file_name) {
          updated[u.file_name] = 100;
        }
      });
      return updated;
    });

    // Remove uploaded files from uploadingFiles array to re-enable the button
    setUploadingFiles((prev) => {
      const remaining = prev.filter((uploadingFile) => {
        if (!uploadingFile) return false;
        // Check if this file has been uploaded - compare against document_name from API
        const isUploaded = uploaded.some((u) => {
          // Extract data from nested response structure
          const uploadData = u?.data?.data || u?.data || u;
          const documentName = uploadData?.document_name || uploadData?.name;
          const uploadingName = uploadingFile.name;

          console.log("Comparing:", {
            documentName,
            uploadingName,
            match: documentName === uploadingName,
          });

          return documentName === uploadingName;
        });
        return !isUploaded; // Keep only files that haven't been uploaded yet
      });

      // Only update if there's a change
      if (remaining.length !== prev.length) {
        console.log(
          "Removing uploaded files from queue. Before:",
          prev.length,
          "After:",
          remaining.length
        );
        return remaining;
      }
      return prev;
    });
  }, [uploadState?.uploaded, uploadingFiles]);

  // Handle upload errors
  useEffect(() => {
    const err = uploadState?.error;
    if (!err) return;
    const message =
      typeof err === "string"
        ? err
        : err.message || err.msg || err || "Upload failed";
    showToast(message, "danger");

    try {
      const slotIndex = err?.slotIndex;
      const fileName =
        err?.file_name || (err?.details && err.details.file_name) || null;
      if (typeof slotIndex === "number") {
        handleFileRemove(fileName || null, slotIndex);
      } else if (fileName) {
        handleFileRemove(fileName);
      }
    } catch (e) {
      // ignore
    }
  }, [uploadState?.error, showToast]);

  // Seed state from incoming projectData
  useEffect(() => {
    if (projectData?.start_date) {
      const d = new Date(projectData.start_date);
      if (!isNaN(d)) setStartDate(d);
    }
    if (projectData?.end_date) {
      const d = new Date(projectData.end_date);
      if (!isNaN(d)) setEndDate(d);
    }

    setClientId(projectData?.client_id ? String(projectData.client_id) : "");
    setPmId(
      projectData?.project_manager_id
        ? String(projectData.project_manager_id)
        : ""
    );
    setPricing(
      projectData?.budget_pricing ? String(projectData.budget_pricing) : ""
    );
    setOffice(projectData?.office_location || "");
    setMarketSegment(projectData?.market_segment || "");

    // Load attachments if available
    const docs =
      projectData?.attachments ||
      projectData?.attachment_data ||
      projectData?.documents ||
      [];
    setAttachments(Array.isArray(docs) ? docs : []);

    // Clear upload state when component mounts
    DISPATCH({ type: CLEAR_UPLOADED_FILES, payload: null });
  }, [projectData, DISPATCH]);

  /* ---------- Options & Labels (same pattern as AddProject) ---------- */

  // Clients
  const clientOptions = useMemo(
    () =>
      (clients || []).map((c) => ({
        // Show only the client name (do not append the ID)
        label: `${c.client_name || c.name || ""}`,
        value: String(c.client_id ?? c.id ?? ""),
      })),
    [clients]
  );
  const clientOptionLabels = useMemo(
    () => clientOptions.map((o) => o.label),
    [clientOptions]
  );
  const currentClientLabel = useMemo(() => {
    const match = clientOptions.find((o) => o.value === clientId);
    if (match) return match.label;
    // Fallback to projectData.client (API may already return the client name)
    if (projectData?.client) return projectData.client;
    if (projectData?.client_name) return projectData.client_name;
    return "";
  }, [clientOptions, clientId, projectData]);

  // Employees
  const employeeOptions = useMemo(
    () =>
      (employees || []).map((e) => ({
        // Show only the employee full name (do not append EMP code)
        // Support multiple possible id keys returned by the API
        label: `${e.first_name || e.firstName || ""} ${
          e.last_name || e.lastName || ""
        }`.trim(),
        value: String(e.employee_id ?? e.emp_id ?? e.employeeId ?? e.id ?? ""),
      })),
    [employees]
  );
  const employeeOptionLabels = useMemo(
    () => employeeOptions.map((o) => o.label),
    [employeeOptions]
  );
  const currentManagerLabel = useMemo(() => {
    const match = employeeOptions.find((o) => o.value === pmId);
    if (match) return match.label;
    // Fallback: if projectData includes manager names, show them even if employees list hasn't loaded/matched yet
    if (
      projectData?.project_manager_first_name ||
      projectData?.project_manager_last_name
    ) {
      return `${projectData.project_manager_first_name || ""} ${
        projectData.project_manager_last_name || ""
      }`.trim();
    }
    return "";
  }, [employeeOptions, pmId, projectData]);

  // Fee Proposal
  const feeLabels = ["Fixed", "Hourly"];
  const currentFeeLabel = useMemo(
    () => (pricing === "1" ? "Fixed" : pricing === "2" ? "Hourly" : ""),
    [pricing]
  );

  // Office — includes Hamilton, Auckland, Napier, Rotorua, Thames, Wellington
  const officeLabels = [
    "Hamilton",
    "Auckland",
    "Napier",
    "Rotorua",
    "Thames",
    "Wellington",
  ];

  // Segments
  const segmentLabels = [
    "Architecture",
    "Assessment",
    "Cultural",
    "Electrical",
    "Environmental",
    "Flooding",
    "Geotech",
    "Heritage",
    "Infrastructure",
    "Insurance",
    "Peer Review",
    "Planning consents",
    "Seismic",
    "Structural",
    "Surveying",
    "Three waters",
    "Training",
    "Transport",
    "Wind Assessment",
  ];

  /* ---------------- Submit ---------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    // Combine existing attachments and newly uploaded ones
    const existingAttachmentIds = attachments
      .map((a) => a?.document_id || a?.id)
      .filter((id) => id !== null && id !== undefined);

    const newAttachmentIds = (uploadState?.uploaded || [])
      .map((d) => {
        const docId =
          d?.document_id || d?.data?.document_id || d?.id || d?.data?.id;
        return docId;
      })
      .filter(
        (id) => id !== null && id !== undefined && !Number.isNaN(Number(id))
      )
      .map((id) => Number(id));

    const allAttachments = Array.from(
      new Set([...existingAttachmentIds, ...newAttachmentIds])
    );

    const payload = {
      project_id: projectData?.pid,
      project_name: (e.target.project_name?.value || "").trim(),
      client_id: Number(clientId || ""),
      project_manager: Number(pmId || ""),
      project_type: Number(e.target.project_type?.value || ""),
      office_location: office,
      market_segment: marketSegment,
      budget_pricing: Number(pricing || ""),
      start_date: startDate ? startDate.toLocaleDateString("en-CA") : "",
      end_date: endDate ? endDate.toLocaleDateString("en-CA") : "",
      attachments: allAttachments,
    };

    const required = [
      "project_name",
      "client_id",
      "project_manager",
      "project_type",
      "office_location",
      "market_segment",
      "budget_pricing",
      "start_date",
    ];
    const errors = validateForm(payload, required) || {};
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      showToast("Please fix highlighted fields", "failed");
      setIsSubmitting(false);
      return;
    }

    try {
      const res = await Axios.post(EDIT_PROJECT, payload);
      if (res.status === 200) {
        showToast("Project Updated Successfully!", "success");
        // Clear upload state
        DISPATCH({ type: CLEAR_UPLOADED_FILES, payload: null });
        setIsSubmitting(false);
        if (update?.setCheckUp) update.setCheckUp(!update.checkUp);
        closeModalEdit();
      } else {
        showToast(res?.data?.message || "Failed to update project", "failed");
        setIsSubmitting(false);
      }
    } catch (error) {
      AuthHandler(error.response?.status, setToast, navigate);
      showToast(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to update project",
        "failed"
      );
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-project-container">
      <form className="project-form" onSubmit={handleSubmit}>
        <fieldset
          disabled={isSubmitting}
          style={{ border: "none", padding: 0, margin: 0 }}
        >
          {/* Row 1 */}
          <div className="form-row">
            <div
              className={`form-group flex-item ${
                formErrors.project_name ? "has-error" : ""
              }`}
            >
              <label>Project Name *</label>
              <input
                type="text"
                name="project_name"
                defaultValue={projectData?.project_name || ""}
                onChange={() => clearError("project_name")}
                placeholder="Enter project name"
              />
              {formErrors.project_name && (
                <div className="error-text">{formErrors.project_name}</div>
              )}
            </div>

            <div
              className={`form-group flex-item ${
                formErrors.client_id ? "has-error" : ""
              }`}
            >
              <label>Client *</label>
              <SearchableDropdown
                label="Client"
                options={clientOptionLabels}
                value={currentClientLabel}
                onChange={(label) => {
                  const match = clientOptions.find((o) => o.label === label);
                  setClientId(match ? match.value : "");
                  clearError("client_id");
                }}
                allowAll={false}
                width="100%"
              />
              {/* Mirror to hidden input for payload consistency */}
              <input type="hidden" name="client_id" value={clientId} />
              {formErrors.client_id && (
                <div className="error-text">{formErrors.client_id}</div>
              )}
            </div>
          </div>

          {/* Row 2 */}
          <div className="form-row">
            <div
              className={`form-group flex-item ${
                formErrors.project_manager ? "has-error" : ""
              }`}
            >
              <label>Project Manager *</label>
              <SearchableDropdown
                label="Project Manager"
                options={employeeOptionLabels}
                value={currentManagerLabel}
                onChange={(label) => {
                  const match = employeeOptions.find((o) => o.label === label);
                  setPmId(match ? match.value : "");
                  clearError("project_manager");
                }}
                allowAll={false}
                width="100%"
              />
              <input type="hidden" name="project_manager" value={pmId} />
              {formErrors.project_manager && (
                <div className="error-text">{formErrors.project_manager}</div>
              )}
            </div>

            <div
              className={`form-group flex-item ${
                formErrors.budget_pricing ? "has-error" : ""
              }`}
            >
              <label>Fee Proposal *</label>
              <SearchableDropdown
                label="Fee Proposal"
                options={feeLabels}
                value={currentFeeLabel}
                onChange={(label) => {
                  setPricing(
                    label === "Fixed" ? "1" : label === "Hourly" ? "2" : ""
                  );
                  clearError("budget_pricing");
                }}
                allowAll={false}
                width="100%"
              />
              <input type="hidden" name="budget_pricing" value={pricing} />
              {formErrors.budget_pricing && (
                <div className="error-text">{formErrors.budget_pricing}</div>
              )}
            </div>
          </div>

          {/* Row 3 */}
          <div className="form-row">
            <div
              className={`form-group flex-item ${
                formErrors.office_location ? "has-error" : ""
              }`}
            >
              <label>Office *</label>
              <SearchableDropdown
                label="Office"
                options={officeLabels}
                value={office}
                onChange={(label) => {
                  setOffice(label);
                  clearError("office_location");
                }}
                allowAll={false}
                width="100%"
              />
              <input type="hidden" name="office_location" value={office} />
              {formErrors.office_location && (
                <div className="error-text">{formErrors.office_location}</div>
              )}
            </div>

            <div
              className={`form-group flex-item ${
                formErrors.market_segment ? "has-error" : ""
              }`}
            >
              <label>Market Segment *</label>
              <SearchableDropdown
                label="Market Segment"
                options={segmentLabels}
                value={marketSegment}
                onChange={(label) => {
                  setMarketSegment(label);
                  clearError("market_segment");
                }}
                allowAll={false}
                width="100%"
              />
              <input
                type="hidden"
                name="market_segment"
                value={marketSegment}
              />
              {formErrors.market_segment && (
                <div className="error-text">{formErrors.market_segment}</div>
              )}
            </div>
          </div>

          {/* Row 4 */}
          <div className="form-row">
            <div
              className={`form-group flex-item ${
                formErrors.project_type ? "has-error" : ""
              }`}
            >
              <label>Project Type *</label>
              <div
                className="radio-group"
                onChange={() => clearError("project_type")}
              >
                <label>
                  <input
                    type="radio"
                    name="project_type"
                    value="1"
                    defaultChecked={projectData?.project_type === 1}
                  />{" "}
                  Billable
                </label>
                <label>
                  <input
                    type="radio"
                    name="project_type"
                    value="2"
                    defaultChecked={projectData?.project_type === 2}
                  />{" "}
                  Non Billable
                </label>
              </div>
              {formErrors.project_type && (
                <div className="error-text">{formErrors.project_type}</div>
              )}
            </div>

            <div className="form-row">
              <div
                className={`form-group flex-item ${
                  formErrors.start_date ? "has-error" : ""
                }`}
              >
                <label>Start Date *</label>
                <DatePicker
                  selected={startDate}
                  onChange={(date) => {
                    setStartDate(date);
                    clearError("start_date");
                  }}
                  dateFormat="yyyy-MM-dd"
                />
                <input
                  type="hidden"
                  name="start_date"
                  value={startDate ? startDate.toLocaleDateString("en-CA") : ""}
                />
                {formErrors.start_date && (
                  <div className="error-text">{formErrors.start_date}</div>
                )}
              </div>

              <div
                className={`form-group flex-item ${
                  formErrors.end_date ? "has-error" : ""
                }`}
              >
                <label>End Date</label>
                <DatePicker
                  selected={endDate}
                  onChange={(date) => {
                    setEndDate(date);
                    clearError("end_date");
                  }}
                  dateFormat="yyyy-MM-dd"
                />
                <input
                  type="hidden"
                  name="end_date"
                  value={endDate ? endDate.toLocaleDateString("en-CA") : ""}
                />
                {formErrors.end_date && (
                  <div className="error-text">{formErrors.end_date}</div>
                )}
              </div>
            </div>
          </div>

          {/* Documents Section */}
          <div className="form-row">
            <div className="form-group flex-item" style={{ padding: 0 }}>
              <label>Project Attachments </label>
              <div
                style={{ background: "#f5f5f6", padding: 20, borderRadius: 4 }}
              >
                {/* Existing Attachments */}

                {attachments.map((doc, i) => {
                  const docName =
                    doc?.document_name ||
                    doc?.file_name ||
                    doc?.name ||
                    `Document ${i + 1}`;
                  const docUrl = doc?.blob_path || doc?.url || doc?.uri || null;

                  return (
                    <div
                      key={`existing-${doc?.document_id || doc?.id || i}`}
                      className={`d-flex align-items-center mb-3 attachment-row`}
                    >
                      <div
                        style={{
                          flex: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                        }}
                      >
                        <i
                          className="bi bi-file-earmark-text"
                          style={{ fontSize: 15, color: "#666" }}
                        ></i>
                        <span style={{ fontSize: 12, color: "#333" }}>
                          {docName}
                        </span>
                      </div>

                      {docUrl && (
                        <a
                          href={docUrl}
                          download
                          className=" download-btn"
                          title="Download document"
                        >
                          <i className="bi bi-download"></i>
                        </a>
                      )}

                      <button
                        type="button"
                        className="btn btn-sm btn-link text-danger "
                        title="Remove"
                        onClick={() => handleFileRemove(null, i, true)}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  );
                })}
                <span
                  style={{
                    paddingBottom: 10,
                    fontSize: 12,
                    fontWeight: 300,
                    margin: "block",
                  }}
                >
                  Upload new attachments
                </span>
                {/* New Upload Slots */}
                {(() => {
                  const uploadedLen = uploadState?.uploaded?.length || 0;
                  const slotsCount =
                    Math.max(uploadingFiles.length, uploadedLen) + 1;
                  return Array.from({ length: slotsCount }).map((_, i) => {
                    const uploadedWithIndex = (
                      uploadState?.uploaded || []
                    ).find(
                      (u) =>
                        typeof u?._slotIndex === "number" && u._slotIndex === i
                    );
                    const uploadedFallback =
                      uploadState?.uploaded && uploadState.uploaded[i]
                        ? {
                            name: uploadState.uploaded[i].file_name,
                            _uploaded: true,
                          }
                        : null;
                    const uploadedItem = uploadedWithIndex || uploadedFallback;
                    const slot = uploadingFiles[i] || uploadedItem || null;

                    return (
                      <div
                        className={`d-flex align-items-center ${
                          i < slotsCount - 1 || attachments.length > 0
                            ? "mb-3"
                            : ""
                        }`}
                        style={{ gap: 12 }}
                        key={`new-doc-row-${i}`}
                      >
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Document Name"
                          value={customNames[i] || ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            setCustomNames((prev) => {
                              const arr = [...prev];
                              while (arr.length <= i) arr.push("");
                              arr[i] = v;
                              return arr;
                            });
                          }}
                          style={{ flexBasis: "40%", maxWidth: 320 }}
                        />

                        <div style={{ flex: 1 }}>
                          {slot ? (
                            <div
                              className="progress"
                              style={{
                                height: 4,
                                borderRadius: 6,
                                overflow: "hidden",
                              }}
                            >
                              <div
                                className={getProgressBarColor(
                                  getDisplayProgress(slot.name)
                                )}
                                role="progressbar"
                                style={{
                                  width: `${getDisplayProgress(slot.name)}%`,
                                  height: 10,
                                  transition: "width 0.3s ease-in-out",
                                }}
                                aria-valuenow={getDisplayProgress(slot.name)}
                                aria-valuemin="0"
                                aria-valuemax="100"
                              ></div>
                            </div>
                          ) : (
                            <div />
                          )}
                        </div>

                        {slot ? (
                          <button
                            type="button"
                            className="btn btn-link text-secondary"
                            title="Remove"
                            onClick={() =>
                              handleFileRemove(slot.name, i, false)
                            }
                            style={{ width: 36, height: 36 }}
                          >
                            <i
                              className="bi bi-trash"
                              style={{ fontSize: 18 }}
                            ></i>
                          </button>
                        ) : (
                          <div style={{ width: 36 }} />
                        )}

                        <div>
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => {
                              if (
                                !customNames[i] ||
                                customNames[i].trim() === ""
                              ) {
                                showToast(
                                  "Please enter a document name before uploading",
                                  "warning"
                                );
                                return;
                              }
                              setUploadTargetIndex(i);
                              document
                                .getElementById("editProjectUploadInput")
                                .click();
                            }}
                            style={{ marginLeft: 8 }}
                          >
                            <i className="bi bi-upload me-1"></i> File
                          </button>
                        </div>
                      </div>
                    );
                  });
                })()}

                <input
                  id="editProjectUploadInput"
                  type="file"
                  accept="image/*,application/pdf"
                  style={{ display: "none" }}
                  onChange={handleFileSelect}
                />
              </div>
            </div>
          </div>
        </fieldset>

        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting || uploadingFiles.length > 0}
          >
            {isSubmitting ? "Updating..." : "Update Project"}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={closeModalEdit}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditProject;
